import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Mic, Square, Loader2 } from "lucide-react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";

const MAX_SECONDS = 60;

function pickMime() {
  if (typeof MediaRecorder === "undefined") return null;
  // Whisper accepts webm / mp4 (Safari) — ogg-only browsers fall back to typing
  const t = ["audio/webm;codecs=opus", "audio/webm", "audio/mp4"].find((m) => MediaRecorder.isTypeSupported(m));
  return t || null;
}

// Shared "tell us more" box: type it, or hold the mic and speak — Whisper (whisper-1) transcribes into the field.
export function CommentField({ value, onChange, label = "Anything you'd like to tell us? (optional)", placeholder, testId = "comment", className = "" }) {
  const [mode, setMode] = useState("idle"); // idle | recording | transcribing
  const [seconds, setSeconds] = useState(0);
  const recRef = useRef(null);
  const chunksRef = useRef([]);
  const timerRef = useRef(null);
  const canRecord = typeof navigator !== "undefined" && !!navigator.mediaDevices?.getUserMedia && pickMime() !== null;

  useEffect(() => () => { clearInterval(timerRef.current); recRef.current?.stream?.getTracks().forEach((t) => t.stop()); }, []);

  const transcribe = async (blob) => {
    setMode("transcribing");
    try {
      const fd = new FormData();
      const ext = blob.type.includes("mp4") ? "mp4" : "webm";
      fd.append("audio", blob, `comment.${ext}`);
      const { data } = await api.post("/public/stt", fd, { headers: { "Content-Type": "multipart/form-data" } });
      if (data.text) onChange(value ? `${value.trim()} ${data.text}` : data.text);
      else toast.message("Didn't catch that — try again a little closer to the mic.");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Couldn't transcribe that");
    } finally {
      setMode("idle");
    }
  };

  const stop = () => {
    clearInterval(timerRef.current);
    const rec = recRef.current;
    if (rec && rec.state !== "inactive") rec.stop();
  };

  const start = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mime = pickMime();
      const rec = new MediaRecorder(stream, { mimeType: mime });
      chunksRef.current = [];
      rec.ondataavailable = (e) => e.data.size && chunksRef.current.push(e.data);
      rec.onstop = () => {
        stream.getTracks().forEach((t) => t.stop());
        const blob = new Blob(chunksRef.current, { type: rec.mimeType || mime || "audio/webm" });
        if (blob.size > 0) transcribe(blob);
        else setMode("idle");
      };
      recRef.current = rec;
      rec.start();
      setSeconds(0);
      setMode("recording");
      timerRef.current = setInterval(() => setSeconds((s) => {
        if (s + 1 >= MAX_SECONDS) stop();
        return s + 1;
      }), 1000);
    } catch {
      toast.error("Microphone access was blocked — you can still type your comment.");
    }
  };

  return (
    <div className={className}>
      <div className="flex items-center justify-between mb-1.5">
        <Label className="text-slate-300">{label}</Label>
        {canRecord && (
          <button
            type="button"
            onClick={mode === "recording" ? stop : start}
            disabled={mode === "transcribing"}
            data-testid={`${testId}-mic-btn`}
            aria-pressed={mode === "recording"}
            className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-[11px] font-semibold transition-colors ${
              mode === "recording"
                ? "border-red-500/60 bg-red-500/15 text-red-300 animate-pulse"
                : "border-brand/40 bg-brand/10 text-brand-soft hover:bg-brand/20"
            } disabled:opacity-60`}
          >
            {mode === "transcribing" ? <Loader2 className="h-3 w-3 animate-spin" /> : mode === "recording" ? <Square className="h-3 w-3" /> : <Mic className="h-3 w-3" />}
            {mode === "transcribing" ? "Transcribing…" : mode === "recording" ? `Stop · ${seconds}s` : "Speak instead"}
          </button>
        )}
      </div>
      <Textarea
        data-testid={`${testId}-input`}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder || "Trade-in, financing, best time to reach you, questions about this vehicle…"}
        className="bg-slate-950 border-slate-800 focus:border-brand text-slate-100 placeholder:text-slate-600 min-h-[84px]"
      />
      {mode === "recording" && (
        <p className="text-[11px] text-red-300 mt-1" data-testid={`${testId}-recording-note`}>Listening — tap Stop when you're done (up to {MAX_SECONDS}s).</p>
      )}
    </div>
  );
}
